import _ from 'lodash';
import { SegmentedButtons, Table, Tooltip, Typography, Button } from '@scale/scaleui';
import { useState, useMemo, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import LookupJSON from 'react-scale/corp/views/CorpProjectGroupManager/views/Lookup/shared/OriginalJson';
interface JsonTableViewProps {
  formattedResponse: any; // JSON object or array
  customJSONObject?: any; // JSON object or array
  showViewTypeToggle?: boolean;
  tableValuesClassName?: string;
  tableKeyClassName?: string;
  defaultViewType?: JsonViewType;
  shouldFlattenJSON?: boolean;
}

/**
 * Recursively flattens a nested object or array.
 * For objects, each key is appended using a "." delimiter.
 * For arrays, the index is appended as part of the path.
 *
 * @param data - The JSON data to flatten.
 * @param parentKey - The accumulated key path (used during recursion).
 * @returns An array of objects with `key` (the full path) and `value`.
 */
function flattenJson(data: any, parentKey: string = ''): { key: string; value: any }[] {
  let items: { key: string; value: any }[] = [];

  if (typeof data !== 'object' || data === null) {
    // Base case: data is a primitive value.
    items.push({ key: parentKey, value: data });
  } else if (Array.isArray(data)) {
    // Process each element in the array.
    data.forEach((item, index) => {
      const newKey = parentKey ? `${parentKey}.${index}` : `${index}`;
      items = items.concat(flattenJson(item, newKey));
    });
  } else {
    // Process each property in the object.å
    Object.entries(data).forEach(([k, v]) => {
      const newKey = parentKey ? `${parentKey}.${k}` : k;
      items = items.concat(flattenJson(v, newKey));
    });
  }

  return items;
}

/**
 * Formats a flattened key string.
 * It splits the key on dots, uses dot notation for standard object properties,
 * and if the segment is numeric (i.e. an array index), it wraps the value in square brackets.
 * The final segment is wrapped in both <em> and <strong> (italics and bold).
 *
 * For example, "users.0.email" becomes:
 *   users[0].<strong><em>email</em></strong>
 *
 * @param key - The flattened key string.
 * @returns An array of React nodes representing the formatted key.
 */
function formatKey(key: string) {
  const parts = key.split('.');
  return parts.map((part, index) => {
    const isNumeric = /^\d+$/.test(part);
    // For non-first parts, if not numeric then use a dot prefix.
    const prefix = index > 0 && !isNumeric ? '.' : '';
    const formatted = isNumeric ? `[${part}]` : part;

    if (index === parts.length - 1) {
      // Wrap the final segment in both bold and italics.
      return (
        <strong key={index}>
          {prefix}
          {formatted}
        </strong>
      );
    }
    return (
      <span key={index}>
        {prefix}
        {formatted}
      </span>
    );
  });
}

function PreviousAndNextRowButtons({
  currentRow,
  rowCount,
  setRow,
}: {
  currentRow: number;
  rowCount: number;
  setRow: (newRow: number) => void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <div className="flex items-center text-sm text-gray-500">
        <div>
          Showing row {currentRow + 1} of {rowCount}
        </div>
        <Tooltip
          text={
            <div className="max-w-[200px] overflow-auto">
              Your delivery format produces multiple rows per task. These will be rows in the
              delivery CSV file. Click through to preview each row for the selected task.
            </div>
          }
          target={<FontAwesomeIcon icon="info-circle" />}
          targetClassName="px-1"
          side="top"
        />
      </div>
      <Button
        onClick={() => {
          setRow((currentRow - 1 + rowCount) % rowCount);
        }}
        variant="neutral"
      >
        <FontAwesomeIcon icon="caret-left" />
      </Button>
      <Button onClick={() => setRow((currentRow + 1) % rowCount)} variant="neutral">
        <FontAwesomeIcon icon="caret-right" />
      </Button>
    </div>
  );
}

type JsonViewType = 'Table' | 'JSON';

/**
 * Renders a flattened JSON as a table with two columns.
 * Each row shows the full key path (using '.' as the delimiter) and the corresponding value.
 */
export function JsonTableView(props: JsonTableViewProps) {
  const {
    formattedResponse,
    // ability to send a different JSON object to render instead of the formattedResponse which will be on the table
    customJSONObject,
    showViewTypeToggle,
    tableValuesClassName = '',
    tableKeyClassName = '',
    shouldFlattenJSON = true,
  } = props;

  const [rowIndexToShow, setRowIndexToShow] = useState<number | null>(null);
  const [viewType, setViewType] = useState<JsonViewType>(props.defaultViewType ?? 'Table');

  const columns = useMemo(() => {
    return [
      {
        id: 'key',
        header: 'Key',
        enableSorting: true,
        headerClassName: 'w-96',
        // eslint-disable-next-line react/display-name
        cell: ({ row }: { row: any }) => {
          const key = row.original.key;
          return (
            <div
              className={twMerge('w-96 overflow-auto', tableKeyClassName)}
              style={{
                transition: 'all 0.5s ease-in-out',
              }}
            >
              {key}
            </div>
          );
        },
      },
      {
        id: 'value',
        header: 'Value',
        enableSorting: true,
        // eslint-disable-next-line react/display-name
        cell: ({ row }: { row: any }) => {
          const value = row.original.value;
          // Show the value as ellipsis if it's too long, and add a tooltip with the full value using scaleui-radix
          return (
            <Tooltip
              asChild
              target={
                <div
                  className={twMerge('overflow-auto', tableValuesClassName)}
                  style={{
                    transition: 'all 0.5s ease-in-out',
                  }}
                >
                  <Typography.Subtitle4>{value}</Typography.Subtitle4>
                </div>
              }
              text={
                <div className="max-w-[33vw] overflow-auto" style={{ whiteSpace: 'break-spaces' }}>
                  {value}
                </div>
              }
            />
          );
        },
      },
    ];
  }, [tableValuesClassName, tableKeyClassName]);

  const currentRowToShow = rowIndexToShow ?? 0;
  useEffect(() => {
    if (!_.isArray(formattedResponse)) {
      setRowIndexToShow(null);
    }
  }, [formattedResponse]);

  // Flatten the JSON data.
  const flattenedData = useMemo(() => {
    if (_.isNil(formattedResponse)) {
      return null;
    }
    if (shouldFlattenJSON) {
      return flattenJson(formattedResponse);
    }
    const responseToFlatten = _.isArray(formattedResponse)
      ? formattedResponse[currentRowToShow]
      : formattedResponse;
    return Object.entries(responseToFlatten).map(([k, v]) => ({ key: k, value: v }));
  }, [currentRowToShow, formattedResponse, shouldFlattenJSON]);

  if (flattenedData === null) {
    return null;
  }

  // Define the table columns.

  // Map the flattened data to table rows, rendering only the leaf key as bold.
  const tableData = flattenedData.map(({ key, value }) => {
    return {
      key: <span>{formatKey(key)}</span>,
      value: (
        <div>
          {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
        </div>
      ),
    };
  });

  return (
    <div className="h-full space-y-2 px-3">
      {showViewTypeToggle && (
        <SegmentedButtons
          buttons={[
            { id: 'Table', label: 'Table' },
            { id: 'JSON', label: 'JSON' },
          ]}
          onSelect={s => setViewType(s as JsonViewType)}
          selected={viewType}
        />
      )}
      {viewType === 'Table' &&
        _.isArray(formattedResponse) &&
        formattedResponse.length > 1 &&
        !shouldFlattenJSON && (
          <PreviousAndNextRowButtons
            currentRow={currentRowToShow}
            rowCount={formattedResponse.length}
            setRow={setRowIndexToShow}
          />
        )}
      <div className="h-full">
        {viewType === 'Table' ? (
          <Table
            columns={columns}
            data={tableData}
            defaultPageSize={tableData.length}
            manualPagination={true} // Disables the pagination footer
          />
        ) : (
          <LookupJSON
            name="taskToDeliver"
            lookup_json={customJSONObject ?? formattedResponse}
            downloadButton={false}
            extraProps={{ collapsed: false }}
          />
        )}
      </div>
    </div>
  );
}
